import Typograf from '@a3/chameleon/src/components/basic/Typograf';
import { ColorsEnum } from 'app.types';
import useAnalytics from 'hooks/useAnalytics';
import useValidation from 'hooks/useValidation';
import { useRouter } from 'next/router';
import type { FC, FormEvent } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import type { SchemaOf } from 'yup';

import { yaEvents } from 'constants/yaEvents';

import Button from 'components/basic/Button';
import Cross from 'components/basic/Cross';
import InputText from 'components/basic/InputText';
import { Body4, Caption, H4 } from 'components/basic/Typography';
import SelectBasicStylized from 'components/complex/SelectBasicStylized';
import s from 'components/complex/forms/ApplicationForm/ApplicationForm.module.scss';
import SendStatusProviderForm from 'components/complex/forms/ApplicationForm/components/SendStatusApplicationForm';
import {
  agreementText,
  recaptchaApiKey,
  values,
} from 'components/complex/forms/ApplicationForm/constants';
import type {
  ApplicationFormTypes,
  ApplicationFormYupTypes,
  SelectOptionType,
} from 'components/complex/forms/ApplicationForm/types';

const ApplicationForm: FC<ApplicationFormTypes> = ({
  setShowForm,
  logo,
  comment: _comment = '',
}) => {
  const { sendFormAnalytics } = useAnalytics();
  const router = useRouter();
  const [selectValue, setSelectValue] = useState<SelectOptionType>(values[0]);
  const [firstName, setFirstName] = useState<string>('');
  const [company, setCompany] = useState<string>('');
  const [communication, setCommunication] = useState<string>('');
  const [comment, setComment] = useState<string>(_comment);
  const [dynamicLabelText, setDynamicLabelText] =
    useState<string>('Номер телефона');
  const [dynamicInputType, setDynamicInputType] = useState<'text' | 'mask'>(
    'mask',
  );
  const [dynamicMask, setDynamicMask] = useState<string | undefined>(
    '+7 (000) 000-00-00',
  );
  const [dynamicPlaceholder, setDynamicPlaceholder] =
    useState<string>('+7 (999) 990 00 90');
  const [successSend, setSuccessSend] = useState<boolean>(false);
  const [sendWithError, setSendWithError] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [isAgreementChecked, setIsAgreementChecked] = useState<boolean>(false);
  const [
    isRepresentativeAgreementChecked,
    setIsRepresentativeAgreementChecked,
  ] = useState<boolean>(false);
  const [isNewsletterAgreementChecked, setIsNewsletterAgreementChecked] =
    useState<boolean>(false);
  const schema = useMemo((): SchemaOf<ApplicationFormYupTypes> => {
    let validText = 'Некорректная почта';
    let regExp = /^\w+([-]?\w+)*@\w+([-]?\w+)*(\.\w{2,3})+$/;
    let requiredText = 'Заполните Email';

    if (selectValue.label !== 'Email') {
      regExp =
        /^(\+7|7|8)?[\s\-]?\(?[489]\d{2}\)?[\s\-]?\d{3}[\s\-]?\d{2}[\s\-]?\d{2}$/;
      validText = 'Некорректный номер';
      requiredText = 'Заполните номер';
    }

    return yup.object().shape({
      firstName: yup.string().required('Заполните имя'),
      company: yup.string().required('Заполните название компании'),
      communication: yup
        .string()
        .matches(regExp, validText)
        .required(requiredText),
    });
  }, [selectValue.label]);
  const { validationErrors, doValidate, validateAllFields } =
    useValidation<ApplicationFormYupTypes>(
      { firstName, communication, company },
      schema,
    );

  useEffect(() => {
    setCommunication('');
    if (selectValue.label === 'Email') {
      setDynamicLabelText('Ваш Email');
      setDynamicInputType('text');
      setDynamicMask(undefined);
      setDynamicPlaceholder('email@email.ru');
    } else {
      setDynamicLabelText('Номер телефона');
      setDynamicInputType('mask');
      setDynamicMask('+7 (000) 000-00-00');
      setDynamicPlaceholder('+7 (999) 990 00 90');
    }
  }, [selectValue.label]);

  const handlerForm = (): void => {
    if (typeof setShowForm === 'function') {
      setShowForm(false);
    }
  };

  const sendApplication = async (evt: FormEvent): Promise<void> => {
    evt.preventDefault();
    const isValidationPassed = await validateAllFields();

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    if (isValidationPassed && isAgreementChecked) {
      setButtonLoading(true);

      try {
        let captchaToken = '';

        if (typeof window.grecaptcha !== 'undefined') {
          captchaToken = await window.grecaptcha.execute(recaptchaApiKey, {
            action: 'application_form_request',
          });
        }

        if (!captchaToken) {
          setButtonLoading(false);

          return Promise.reject('captchaToken error');
        }

        const isPhoneMethod = selectValue.label !== 'Email';
        const body = {
          context: {
            source: router.asPath,
            company_from_request: company,
            contact_name: firstName,
            contact_method: [
              {
                name: selectValue.label,
              },
            ],
            ...(isPhoneMethod && {
              contact_phone: [
                {
                  type: 'home',
                  tel: communication.replace(/[\s\-]/g, ''),
                },
              ],
            }),
            ...(selectValue.label === 'Email' && {
              contact_email: [
                {
                  type: 'home',
                  email: communication,
                },
              ],
            }),
            message: comment || undefined,
            agreement_received_offers: isNewsletterAgreementChecked,
          },
        };
        const response = await fetch('/api/elma-customer-request/', {
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify(body),
        });

        if (response.status === 200) {
          const json = await response.json();

          if (json.success && !json.error) {
            setSuccessSend(true);
            sendFormAnalytics(yaEvents.sendApplicationFormSuccess);
          } else {
            setSendWithError(true);
            sendFormAnalytics(yaEvents.sendApplicationFormErr);
          }
        } else {
          setSendWithError(true);
          sendFormAnalytics(yaEvents.sendApplicationFormErr);
          console.error('response ->>', response);
        }

        setButtonLoading(false);
      } catch (error) {
        setSendWithError(true);
        sendFormAnalytics(yaEvents.sendApplicationFormErr);
        console.error('error ->>', error);
        setButtonLoading(false);
      }
    }
  };

  const content = successSend ? (
    <SendStatusProviderForm
      setShowForm={setShowForm}
      sendWithError={sendWithError}
    />
  ) : (
    <form className={s.wrapper} onSubmit={sendApplication}>
      <div className={s.description}>
        <H4 className={s.h4}>Заявка на продукт</H4>
        <Body4 className={s.body4}>
          Укажите свои контакты, чтобы с вами связался специалист из нужного
          отдела.
        </Body4>
        {logo}
      </div>

      <div className={s.inputFields}>
        <InputText
          name="firstName"
          labelText="Имя"
          value={firstName}
          onChange={(newValue): void => {
            setFirstName(newValue);
          }}
          doValidate={doValidate}
          errorMessage={validationErrors.firstName}
          className={s.input}
        />

        <InputText
          name="company"
          labelText="Компания"
          value={company}
          onChange={(newValue): void => {
            setCompany(newValue);
          }}
          doValidate={doValidate}
          errorMessage={validationErrors.company}
          className={s.input}
        />

        <div className={s.flexBlock}>
          <SelectBasicStylized
            labelText="Способ связи"
            value={selectValue}
            options={values}
            onChange={(newValue): void => {
              setSelectValue(newValue);
            }}
          />
          <InputText
            name="communication"
            labelText={dynamicLabelText}
            value={communication}
            onChange={(newValue): void => {
              setCommunication(newValue);
            }}
            doValidate={doValidate}
            errorMessage={validationErrors.communication}
            type={dynamicInputType}
            mask={dynamicMask}
            placeholder={dynamicPlaceholder}
            className={s.phone}
            isShowErrorOnBlur
          />
        </div>

        <InputText
          name="textarea"
          type="textarea"
          labelText="Комментарии (необязательно)"
          value={comment}
          onChange={(newValue): void => {
            setComment(newValue);
          }}
          className={s.textarea}
        />
        <Caption className={s.caption}>
          {agreementText(
            setIsAgreementChecked,
            setIsRepresentativeAgreementChecked,
            setIsNewsletterAgreementChecked,
          )}
        </Caption>
        <Button
          look={ColorsEnum.blue}
          isDark
          className={s.button}
          type="submit"
          isLoading={buttonLoading}
          isDisabled={!isAgreementChecked || !isRepresentativeAgreementChecked}
        >
          Отправить
        </Button>
      </div>
    </form>
  );

  return (
    <Typograf>
      <style global jsx>
        {`
          body {
            overflow: hidden;
          }
        `}
      </style>

      {typeof setShowForm === 'function' && (
        <Cross onClick={handlerForm} className={s.cross} />
      )}
      <div className={s.backdrop} onClick={handlerForm} />
      <div className={s.container}>{content}</div>
    </Typograf>
  );
};

export default ApplicationForm;
